import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScenarioMachineEntryCard } from './ScenarioMachineEntryCard';
import { GitBranch, AlertCircle, Plus, HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ScenarioConfig, ScenarioMachineEntry } from '@/types/flexe';
import { computeKW, validateMachineSpec } from '@/lib/flexeKWConversion';

interface FlexEScenarioBuilderProps {
  uploadId: string;
  onScenarioChange: (config: ScenarioConfig | null) => void;
}

export default function FlexEScenarioBuilder({
  uploadId,
  onScenarioChange,
}: FlexEScenarioBuilderProps) {
  const [enabled, setEnabled] = useState(false);
  const [windowStart, setWindowStart] = useState('16:00');
  const [windowEnd, setWindowEnd] = useState('21:00');
  const [entries, setEntries] = useState<ScenarioMachineEntry[]>([]);

  // Notify parent when config changes
  useEffect(() => {
    if (!enabled) {
      onScenarioChange(null);
      return;
    }

    const config: ScenarioConfig = {
      enabled: true,
      window: { start: windowStart, end: windowEnd },
      machines: entries,
    };

    onScenarioChange(config);
  }, [enabled, windowStart, windowEnd, entries, onScenarioChange]);

  // Calculate total delta
  let totalDelta = 0;
  const validEntries: Array<{ index: number; delta: number; error: string | null }> = [];

  entries.forEach((entry, index) => {
    try {
      const newError = validateMachineSpec(entry.new_machine);
      const oldError = entry.type === 'replacement' && entry.old_machine ? validateMachineSpec(entry.old_machine) : null;

      if (newError || oldError) {
        validEntries.push({ index, delta: 0, error: newError || oldError });
        return;
      }

      const delta = entry.type === 'new'
        ? computeKW(entry.new_machine)
        : computeKW(entry.new_machine) - (entry.old_machine ? computeKW(entry.old_machine) : 0);

      validEntries.push({ index, delta, error: null });
      totalDelta += delta;
    } catch (e: any) {
      validEntries.push({ index, delta: 0, error: e.message });
    }
  });

  const hasErrors = validEntries.some(v => v.error !== null);
  const isValid = entries.length > 0 && !hasErrors;

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <GitBranch className="w-5 h-5" />
        <h2 className="text-xl font-semibold">What-If Scenario (Optioneel)</h2>
      </div>

      <div className="space-y-4">
        {/* Enable Toggle */}
        <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
          <Label htmlFor="scenario-enable" className="flex-1 cursor-pointer">
            Simuleer capaciteitsimpact van nieuwe/vervangen machines
          </Label>
          <Switch
            id="scenario-enable"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>

        {enabled && (
          <>
            {/* Capaciteitsvenster */}
            <div className="space-y-2">
              <Label>Capaciteitsvenster</Label>
              <p className="text-xs text-muted-foreground">
                Scenario's worden alleen toegepast tijdens dit venster (piekuren met capaciteitsbeperking)
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="window-start" className="text-xs">Start</Label>
                  <Input
                    id="window-start"
                    type="time"
                    value={windowStart}
                    onChange={(e) => setWindowStart(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="window-end" className="text-xs">Einde</Label>
                  <Input
                    id="window-end"
                    type="time"
                    value={windowEnd}
                    onChange={(e) => setWindowEnd(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Machine Entries */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Machines</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-sm">
                      <p className="font-semibold mb-1">Machine types:</p>
                      <ul className="space-y-1 text-sm">
                        <li>• <strong>Nieuwe:</strong> Volledig kW wordt toegevoegd tijdens capaciteitsvenster</li>
                        <li>• <strong>Vervanging:</strong> Alleen verschil (ΔkW = nieuw - oud) wordt toegepast</li>
                      </ul>
                      <p className="mt-2 text-sm">
                        Je kunt kW direct invoeren, of berekenen via Ampère + cosφ + fasen + voltage
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              {/* Entry List */}
              <div className="space-y-3">
                {entries.map((entry, index) => (
                  <ScenarioMachineEntryCard
                    key={index}
                    entry={entry}
                    index={index}
                    onChange={(updated) => {
                      const newEntries = [...entries];
                      newEntries[index] = updated;
                      setEntries(newEntries);
                    }}
                    onRemove={() => setEntries(entries.filter((_, i) => i !== index))}
                  />
                ))}

                {/* Add Entry Button */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() =>
                    setEntries([
                      ...entries,
                      {
                        type: 'new',
                        new_machine: {},
                        old_machine: undefined,
                      },
                    ])
                  }
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Voeg machine toe
                </Button>
              </div>
            </div>

            {/* Delta Preview */}
            {isValid && (
              <Alert>
                <AlertTitle>Totaal ΔkW in venster ({windowStart} - {windowEnd})</AlertTitle>
                <AlertDescription>
                  <div className="space-y-1">
                    {validEntries.map((v, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span>Machine {v.index + 1}:</span>
                        <span className={v.delta >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                          {v.delta >= 0 ? '+' : ''}{v.delta.toFixed(1)} kW
                        </span>
                      </div>
                    ))}
                    <div className="font-bold pt-2 border-t mt-2">
                      Totaal: <span className={totalDelta >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {totalDelta >= 0 ? '+' : ''}{totalDelta.toFixed(1)} kW
                      </span>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Validation Errors */}
            {entries.length > 0 && hasErrors && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Incompleet scenario</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-4 space-y-1">
                    {validEntries.map((v, i) =>
                      v.error ? (
                        <li key={i} className="text-sm">
                          Machine {v.index + 1}: {v.error}
                        </li>
                      ) : null
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {entries.length === 0 && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Geen machines</AlertTitle>
                <AlertDescription>
                  Voeg minimaal 1 machine toe om een scenario te simuleren
                </AlertDescription>
              </Alert>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
